import type { StatusChipTone } from '@/components/ui/status-chip';
import type { AreaCoverage } from '@/lib/api/discovery';
import type {
  CoverageChange,
  EvidenceChannel,
  EvidenceLabel,
  EvidenceStatus,
  FollowUpQuestion,
  PainDismissReason,
  QuotedEvidence,
  Severity,
  WithdrawResult,
} from '@/lib/api/discovery-findings';
import { formatDay, plural } from '../discovery-logic';

/**
 * Pure helpers behind Discovery -> Findings (T-0188). They word and
 * arrange what the API computed; nothing here counts sources, coverage
 * or support. Design reference: `findingsView`, `domainBody`, `painRow`,
 * `inlineForm` and `allEvidenceBody` in `docs/discovery/prototype/discovery.js`.
 */

/** The area's line in the list and its head chip: "Covered · 3 sources" (Decision 53.6). */
export function areaStatusLine(a: Pick<AreaCoverage, 'status' | 'status_label' | 'sources'>) {
  return a.status === 'corroborated'
    ? `${a.status_label} · ${plural(a.sources, 'source')}`
    : a.status_label;
}

/**
 * `?area=untagged`: the pain points no area lists (no active area of the
 * pinned version). Area ids are uuids, so the word cannot name an area.
 */
export const UNTAGGED_AREA = 'untagged';

/** What a quote describes, in words (T-0285): the five kinds the readers look for. */
export const EVIDENCE_LABEL_TEXT: Record<EvidenceLabel, string> = {
  how_it_runs: 'How it runs',
  volumes_timing: 'Volumes and timing',
  systems_files: 'Systems and files',
  problems: 'Problems',
  needs: 'Needs',
};

/** Without an area in the address: the first area that still needs something, else the first. */
export function defaultAreaId(areas: ReadonlyArray<AreaCoverage>): string | null {
  return (areas.find((a) => a.status !== 'corroborated') ?? areas[0])?.area_id ?? null;
}

export const CHANNEL_LABEL: Record<EvidenceChannel, string> = {
  interview: 'Interview',
  questionnaire: 'Questionnaire',
  follow_up: 'Follow-up',
  document: 'Document',
  data: 'Data extract',
};

export const EVIDENCE_STATUS_CHIP: Record<EvidenceStatus, { label: string; tone: StatusChipTone }> =
  {
    confirmed: { label: 'Confirmed', tone: 'healthy' },
    proposed: { label: 'Awaiting review', tone: 'ai' },
    declined: { label: 'Declined', tone: 'frozen' },
    withdrawn: { label: 'Withdrawn', tone: 'frozen' },
    superseded: { label: 'Superseded', tone: 'frozen' },
  };

/** The ledger's status filters, in the prototype's order. */
export const LEDGER_STATUSES = ['confirmed', 'proposed', 'withdrawn', 'declined'] as const;
export type LedgerStatusFilter = 'all' | (typeof LEDGER_STATUSES)[number];

export function ledgerStatusOptions(counts: Record<string, number> | undefined) {
  const n = (k: string) => counts?.[k] ?? 0;
  const all = Object.values(counts ?? {}).reduce((a, b) => a + b, 0);
  return [
    { value: 'all' as const, label: 'All', count: all },
    ...LEDGER_STATUSES.map((s) => ({
      value: s,
      label: EVIDENCE_STATUS_CHIP[s].label,
      count: n(s),
    })),
  ];
}

export function parseLedgerStatus(v: string | undefined): LedgerStatusFilter {
  return (LEDGER_STATUSES as readonly string[]).includes(v ?? '')
    ? (v as LedgerStatusFilter)
    : 'all';
}

/** "Withdrawn on 6 Oct 2026: “It was about the old system”. It stays on record…" */
export function withdrawnLine(e: Pick<QuotedEvidence, 'withdraw_reason' | 'withdrawn_at'>) {
  const on = e.withdrawn_at ? ` on ${formatDay(e.withdrawn_at)}` : '';
  const why = (e.withdraw_reason ?? '').replace(/[.\s]+$/, '');
  return `Withdrawn${on}${why ? `: “${why}”` : ''}. It stays on record and no longer counts.`;
}

/** Areas that became covered, in words, for a toast. */
export function coverageFlipText(changes: ReadonlyArray<CoverageChange>): string {
  const up = changes
    .filter((c) => c.status_after === 'corroborated' && c.status_before !== 'corroborated')
    .map((c) => c.label ?? 'An area');
  const down = changes
    .filter((c) => c.status_before === 'corroborated' && c.status_after !== 'corroborated')
    .map((c) => c.label ?? 'An area');
  const parts: string[] = [];
  if (up.length) parts.push(`${up.join(', ')} ${up.length === 1 ? 'is' : 'are'} now covered.`);
  if (down.length) {
    parts.push(`${down.join(', ')} ${down.length === 1 ? 'is' : 'are'} no longer covered.`);
  }
  return parts.join(' ');
}

/** What a withdrawal left behind (Decision 54.20), in one toast. */
export function withdrawnToast(number: string | null, r: WithdrawResult): string {
  const parts = [`${number ?? 'The item'} withdrawn. It stays on record and no longer counts.`];
  const flip = coverageFlipText(r.coverage_changes);
  if (flip) parts.push(flip);
  const pp = r.cascade.pain_points_flagged.map((p) => p.number ?? 'a proposed pain point');
  if (pp.length) parts.push(`${pp.join(', ')} now ${pp.length === 1 ? 'needs' : 'need'} evidence.`);
  const steps = r.cascade.map_steps_flagged.length;
  if (steps)
    parts.push(`${plural(steps, 'map step')} now ${steps === 1 ? 'needs' : 'need'} evidence.`);
  const sug = r.cascade.suggestions_withdrawn.length;
  if (sug)
    parts.push(
      `${plural(sug, 'requirement suggestion')} built only on it ${sug === 1 ? 'was' : 'were'} withdrawn.`,
    );
  const req = r.cascade.requirements_citing.map((q) => q.number);
  if (req.length)
    parts.push(`${req.join(', ')} ${req.length === 1 ? 'cites' : 'cite'} it, so sign-off reopens.`);
  return parts.join(' ');
}

// ---------------------------------------------------------------------------
// Pain points (Decisions 43, 53.14, 54.20, 54.23, 54.24)
// ---------------------------------------------------------------------------

export const SEVERITY_LABEL: Record<Severity, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

export const SEVERITY_TONE: Record<Severity, StatusChipTone> = {
  critical: 'critical',
  high: 'at-risk',
  medium: 'neutral',
  low: 'unassessed',
};

export const SEVERITY_OPTIONS = (['low', 'medium', 'high', 'critical'] as const).map((s) => ({
  value: s,
  label: SEVERITY_LABEL[s],
}));

/** The reasons offered when dismissing a confirmed pain point (54.24); "Other" takes a few words. */
export const PAIN_DISMISS_LABEL: Record<PainDismissReason, string> = {
  not_a_real_problem: 'Not a real problem',
  covered_by_another: 'Covered by another pain point',
  out_of_scope: 'Out of scope',
  other: 'Other',
};

/** The reason to send, or null while it is not complete. */
export function dismissReasonOf(
  choice: PainDismissReason | null,
  other: string,
): { code: PainDismissReason; text?: string } | null {
  if (!choice) return null;
  if (choice !== 'other') return { code: choice };
  return other.trim() ? { code: 'other', text: other.trim() } : null;
}

/** The reason in words, for a toast. */
export const dismissReasonText = (r: { code: PainDismissReason; text?: string }) =>
  r.code === 'other' ? (r.text ?? '') : PAIN_DISMISS_LABEL[r.code];

/** Why the hand-added pain point cannot be saved yet, or null. */
export function manualPainProblem(text: string, picked: number, severity: Severity | null) {
  if (!text.trim()) return 'Write the pain point';
  if (picked === 0) return 'Pick at least one confirmed evidence item';
  if (!severity) return 'Set a severity';
  return null;
}

// ---------------------------------------------------------------------------
// Follow-ups (rule 19, Decision 53.13)
// ---------------------------------------------------------------------------

export interface FollowUpPicks {
  framework: ReadonlySet<string>;
  ai: ReadonlySet<string>;
  custom: string;
}

export function followUpQuestions(p: FollowUpPicks): FollowUpQuestion[] {
  return [
    ...[...p.framework].map((question_id) => ({ question_id })),
    ...[...p.ai].map((ai_proposal_id) => ({ ai_proposal_id })),
    ...(p.custom.trim() ? [{ text: p.custom.trim() }] : []),
  ];
}

export const followUpCountOk = (n: number, max: number) => n >= 1 && n <= max;

/** Map state words on the area page (`.done-row.link` in `domainBody`). */
export function mapStateText(state: string): string {
  return (
    {
      validated: 'Current state validated',
      draft: 'Draft',
      ready: 'Ready to draft',
      waiting: 'Needs evidence',
    }[state] ?? state
  );
}

/** The evidence numbers in order, E-001 before E-010. */
export const byNumber = (a: { number: string | null }, b: { number: string | null }) =>
  (a.number ?? '').localeCompare(b.number ?? '', undefined, { numeric: true });
